import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { AlertTriangle, Building2, CheckCircle2, Clock, Download, Plus } from 'lucide-react';
import { toast } from 'sonner';

import { IrregularidadeCell } from '@/modulos/operacao/comum/IrregularidadeCell';
import { UltimaSincronizacaoCell } from '@/modulos/operacao/comum/UltimaSincronizacaoCell';
import { situacaoComoSelo } from '@/modulos/operacao/comum/contrato';
import { useOpcoesDeDre } from '@/modulos/operacao/comum/dados';
import { useUrlFilters } from '@/nucleo/ganchos/useUrlFilters';
import { DataTable, type DataTableColumn } from '@/ui/DataTable';
import { ErrorState } from '@/ui/ErrorState';
import { FilterBar, type FilterFieldConfig } from '@/ui/FilterBar';
import { PageHeader } from '@/ui/PageHeader';
import { StatCard } from '@/ui/StatCard';
import { StatusBadge } from '@/ui/StatusBadge';
import { TipoPessoaBadge } from '@/ui/TipoPessoaBadge';
import { Button } from '@/ui/base/button';
import { Checkbox } from '@/ui/base/checkbox';
import { Label } from '@/ui/base/label';
import { MultiSelectCombobox } from '@/ui/base/multi-select-combobox';
import { ResponsiveModal } from '@/ui/ResponsiveModal';
import { useTableViewPreferences } from '@/ui/table-view/useTableViewPreferences';
import type { TableViewColumn } from '@/ui/table-view/tabelaVisoes';

import { BarraDeAcoesEmLote } from './BarraDeAcoesEmLote';
import { CredenciadoFormulario } from './CredenciadoFormulario';
import { LicencaPlacaCell } from './LicencaPlacaCell';
import { OPCOES_DE_TIPO, type Credenciado, type FiltroDeCredenciados } from './contrato';
import { useBaixarCredenciadosCsv, useCredenciados, useIndicadoresDeCredenciados } from './dados';

/**
 * `/operacao/credenciados` — as empresas, pessoas fisicas e cooperativas COM TERMO
 * ATIVO no programa.
 *
 * E a porta de entrada do credenciamento, e por isso ela e a listagem mais pesada das
 * cinco: tem indicadores no topo, filtro por diretoria, selecao em lote e a frota
 * resumida na linha. Nada disso e enfeite — cada peca responde uma pergunta que a
 * operacao faz todo dia, e as razoes de cada uma estao junto do codigo que a monta.
 *
 * DADO REAL, DA API (`dados.ts`, `bff/v1/operacao/credenciados`). Lista, indicadores
 * e CSV respeitam o recorte do banco: quem e de DRE ve so a propria diretoria. CPF/CNPJ,
 * e-mail e telefone chegam MASCARADOS e aparecem como vieram — sem botao de copiar,
 * porque copiar um valor mascarado nao serve a ninguem; o contato inteiro esta na aba
 * Dados Gerais do detalhe, onde a leitura fica registrada.
 *
 * O QUE ESTA TELA DELIBERADAMENTE NAO FAZ, e por que:
 *
 * - PENDENCIA FINANCEIRA NAO TEM FILTRO, E A COLUNA FICA VAZIA. E juizo derivado das
 *   cobrancas administrativas, que ninguem apurou ainda; mesma saida da coluna `Saude`
 *   da Parametrizacao (CLAUDE.md, regra 7 de interface): a estrutura fica, a
 *   afirmacao nao.
 * - "NOVO CREDENCIADO" ABRE O FORMULARIO, MAS O "SALVAR" NAO GRAVA NADA AINDA.
 *   Nenhuma tabela cadastral aceita escrita hoje — nem de `ADMIN` (docs/ALVOS.md,
 *   Onda 2) —, e nao ha rota de criacao na API. O dono do repositorio decidiu, em
 *   2026-08-20, que a interface pode ficar pronta antes do backend: o botao e o
 *   formulario existem, e o "Salvar" mostra um aviso honesto em vez de fingir sucesso
 *   (`comum/cadastroIndisponivel.ts`) — nunca um erro tecnico nem uma gravacao
 *   simulada.
 * - A LINHA INTEIRA LEVA AO DETALHE (`/operacao/credenciados/:slug`), regra de toda
 *   listagem com tela de detalhe (CLAUDE.md, "Regras de interface", item 9) — nao
 *   existe botao "Ver" a parte.
 */
export function CredenciadosPage() {
  const navigate = useNavigate();

  const padroes = useMemo(() => ({ search: '', status: '', tipo: '', dres: '' }), []);
  const { values, setFilter, clearAll, page, setPage, perPage, setPerPage } =
    useUrlFilters(padroes);

  const dresSelecionadas = useMemo(
    () => (values.dres ? values.dres.split(',').filter(Boolean) : []),
    [values.dres],
  );
  const opcoesDeDre = useOpcoesDeDre();

  const filtro = useMemo<FiltroDeCredenciados>(
    () => ({
      pagina: page,
      tamanho: perPage,
      busca: values.search || undefined,
      situacao: values.status || undefined,
      tipo: values.tipo || undefined,
      dreIds: dresSelecionadas.length > 0 ? dresSelecionadas : undefined,
    }),
    [page, perPage, values.search, values.status, values.tipo, dresSelecionadas],
  );

  const { data, isLoading, isError, error, refetch } = useCredenciados(filtro);
  const { data: indicadores } = useIndicadoresDeCredenciados();
  const baixarCsv = useBaixarCredenciadosCsv();
  const [baixando, setBaixando] = useState(false);
  const [novoAberto, setNovoAberto] = useState(false);

  const linhas = useMemo(() => data?.itens ?? [], [data?.itens]);

  // ─── Seleção em lote ───────────────────────────────────────────────────────
  /**
   * A selecao ATRAVESSA PAGINAS de proposito: quem precisa suspender doze credenciados
   * espalhados pelo cadastro nao consegue faze-lo numa pagina so. Guardar identificador,
   * e nao a linha inteira, e o que permite isso sem manter em memoria dados de paginas
   * que ja sairam da tela.
   */
  const [selecionados, setSelecionados] = useState<string[]>([]);

  const idsDaPagina = useMemo(() => linhas.map((linha) => linha.id), [linhas]);
  const paginaToda = idsDaPagina.length > 0 && idsDaPagina.every((id) => selecionados.includes(id));

  const alternarPagina = useCallback(() => {
    setSelecionados((atuais) => {
      if (idsDaPagina.length > 0 && idsDaPagina.every((id) => atuais.includes(id))) {
        return atuais.filter((id) => !idsDaPagina.includes(id));
      }
      return Array.from(new Set([...atuais, ...idsDaPagina]));
    });
  }, [idsDaPagina]);

  const alternarLinha = useCallback((id: string) => {
    setSelecionados((atuais) =>
      atuais.includes(id) ? atuais.filter((outro) => outro !== id) : [...atuais, id],
    );
  }, []);

  const temFiltro = Boolean(values.search || values.status || values.tipo || values.dres);

  const campos: FilterFieldConfig[] = [
    {
      key: 'search',
      label: 'Busca',
      type: 'search',
      placeholder: 'Buscar por nome, CPF/CNPJ, e-mail ou telefone…',
      ariaLabel: 'Buscar credenciados',
      flex: true,
    },
    {
      key: 'tipo',
      label: 'Tipo',
      type: 'select',
      options: [...OPCOES_DE_TIPO],
      width: 'w-full sm:w-[150px]',
    },
    {
      key: 'status',
      label: 'Situação',
      type: 'select',
      options: [
        { value: 'ATIVO', label: 'Ativo' },
        { value: 'INATIVO', label: 'Inativo' },
        { value: 'SUSPENSO', label: 'Suspenso' },
        { value: 'BLOQUEADO', label: 'Bloqueado' },
      ],
      width: 'w-full sm:w-[150px]',
    },
  ];

  const colunas = useMemo<TableViewColumn<Credenciado>[]>(
    () => [
      {
        id: 'select',
        header: '',
        // Coluna de seleção sempre primeira (CLAUDE.md, "Regras de interface", item 10).
        isSelectionColumn: true,
        headerClassName: 'w-10',
        cellClassName: 'w-10',
        cell: (credenciado) => (
          <Checkbox
            checked={selecionados.includes(credenciado.id)}
            onCheckedChange={() => alternarLinha(credenciado.id)}
            aria-label={`Selecionar ${credenciado.nome}`}
          />
        ),
      },
      {
        id: 'nome',
        header: 'Nome',
        cell: (credenciado) => <span className="font-medium">{credenciado.nome}</span>,
        sortKey: 'nome',
        isCritical: true,
        canHide: false,
      },
      {
        id: 'cpf_cnpj',
        header: 'CPF/CNPJ',
        cell: (credenciado) => (
          <span className="font-mono text-sm whitespace-nowrap">{credenciado.cpf_cnpj || '—'}</span>
        ),
        tooltip: 'Mascarado. O documento inteiro não sai do banco.',
      },
      {
        id: 'dre',
        header: 'DRE',
        cell: (credenciado) => <span className="text-sm">{credenciado.dres?.codigo ?? '—'}</span>,
        sortKey: (credenciado) => credenciado.dres?.codigo ?? '',
        linkedFilterKeys: ['dres'],
        tooltip: 'Diretoria Regional de Ensino a que o credenciado está vinculado.',
      },
      {
        id: 'tipo',
        header: 'Tipo',
        cell: (credenciado) => <TipoPessoaBadge tipo={credenciado.tipo} />,
        sortKey: 'tipo',
        linkedFilterKeys: ['tipo'],
      },
      {
        id: 'email',
        header: 'E-mail',
        cell: (credenciado) => <span className="text-sm">{credenciado.email || '—'}</span>,
        tooltip: 'Mascarado. O contato inteiro está na aba Dados Gerais do detalhe.',
      },
      {
        id: 'telefone',
        header: 'Telefone',
        cell: (credenciado) => (
          <span className="font-mono text-sm whitespace-nowrap">{credenciado.telefone || '—'}</span>
        ),
        tooltip: 'Mascarado. O contato inteiro está na aba Dados Gerais do detalhe.',
      },
      {
        id: 'licenca_placa',
        header: 'Licença / Placa',
        cell: (credenciado) => <LicencaPlacaCell veiculos={credenciado.veiculos} somenteAtivos />,
        tooltip: 'Licença municipal (CRM) e placa dos veículos ativos deste credenciado.',
      },
      {
        id: 'pendencias',
        header: 'Pendências',
        // Vazia de propósito: ver o comentário do topo.
        cell: () => (
          <span className="text-muted-foreground text-sm">
            <span aria-hidden="true">—</span>
            <span className="sr-only">Ainda não apurada</span>
          </span>
        ),
        tooltip: 'Ainda não apurada: depende das cobranças administrativas, que esta tela não lê.',
      },
      {
        id: 'irregularidade',
        header: 'Irregularidade',
        cell: (credenciado) => <IrregularidadeCell situacao={credenciado.status} />,
      },
      {
        id: 'ultima_sincronizacao',
        header: 'Última sincronização',
        cell: (credenciado) => (
          <UltimaSincronizacaoCell
            origem={credenciado.origem_dado}
            sincronizadoEm={credenciado.sincronizado_em}
            emContingencia={credenciado.em_contingencia}
          />
        ),
        sortKey: 'sincronizado_em',
      },
      {
        id: 'status',
        header: 'Situação',
        cell: (credenciado) => (
          <StatusBadge status={situacaoComoSelo(credenciado.status)} label={credenciado.status} />
        ),
        sortKey: 'status',
        linkedFilterKeys: ['status'],
        isCritical: true,
        canHide: false,
        isStatusColumn: true,
      },
    ],
    [alternarLinha, selecionados],
  );

  const tableView = useTableViewPreferences({
    contextKey: 'credenciados:list',
    columns: colunas,
    filterValues: values,
  });

  const exportar = async () => {
    setBaixando(true);
    try {
      const { quantidade, total } = await baixarCsv(filtro);
      toast('Planilha gerada', {
        description:
          quantidade < total
            ? `${quantidade} de ${total} credenciado(s) no arquivo, o limite de uma exportação. Use os filtros para dividir o recorte.`
            : `${quantidade} credenciado(s) no arquivo, com os filtros que você está usando.`,
      });
    } catch {
      toast.error('Não foi possível gerar o arquivo', {
        description: 'Tente novamente em instantes.',
      });
    } finally {
      setBaixando(false);
    }
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title="Credenciados"
        description="Empresas, pessoas físicas e cooperativas com termo de adesão ativo no programa TEG."
      >
        <Button
          variant="outline"
          onClick={() => void exportar()}
          disabled={baixando || isLoading}
          aria-label="Exportar credenciados em CSV"
        >
          <Download className="h-4 w-4 sm:mr-2" aria-hidden="true" />
          <span className="hidden sm:inline">{baixando ? 'Gerando…' : 'Exportar CSV'}</span>
        </Button>
        <Button onClick={() => setNovoAberto(true)}>
          <Plus className="h-4 w-4 sm:mr-2" aria-hidden="true" />
          <span className="hidden sm:inline">Novo Credenciado</span>
        </Button>
      </PageHeader>

      <ResponsiveModal
        open={novoAberto}
        onOpenChange={setNovoAberto}
        title={
          <>
            <Building2 className="h-5 w-5 text-primary" aria-hidden="true" />
            Novo Credenciado
          </>
        }
        description="Cadastre um novo credenciado no programa TEG."
        desktopMaxWidth="max-w-4xl"
      >
        <CredenciadoFormulario onCancelar={() => setNovoAberto(false)} />
      </ResponsiveModal>

      {/*
        Os indicadores só entram quando a API responde. Cartão com zero enquanto carrega
        seria número inventado, e número em tela de sistema público vem de dado real ou
        não vem (CLAUDE.md, "Proibido").
      */}
      {indicadores && (
        <div className="border-l-success grid grid-cols-1 gap-4 border-l-4 pl-3 md:grid-cols-3">
          <StatCard title="Ativos" value={indicadores.ativos} icon={CheckCircle2} />
          <StatCard title="Novos (30 dias)" value={indicadores.novos_30d} icon={Clock} />
          <StatCard
            title="Suspensos ou bloqueados"
            value={indicadores.suspensos_ou_bloqueados}
            icon={AlertTriangle}
          />
        </div>
      )}

      <FilterBar
        fields={campos}
        values={values}
        onChange={setFilter}
        onClear={clearAll}
        defaultValues={padroes}
      >
        <div className="space-y-1">
          <Label className="text-muted-foreground text-xs uppercase tracking-wider">DRE</Label>
          <MultiSelectCombobox
            options={opcoesDeDre}
            selected={dresSelecionadas}
            onChange={(ids) => setFilter('dres', ids.join(','))}
            placeholder="Todas as DREs"
            searchPlaceholder="Buscar DRE…"
            emptyMessage="Nenhuma DRE encontrada."
            renderSelectedText={(quantas) =>
              `${quantas} DRE${quantas > 1 ? 's' : ''} selecionada${quantas > 1 ? 's' : ''}`
            }
            triggerClassName="sm:w-[220px]"
          />
        </div>
      </FilterBar>

      {/*
        Erro tem tela própria, e ela vem ANTES da tabela — não como linha vazia dentro
        dela. Tabela vazia por falha de rede é indistinguível de "não há registros", e as
        duas pedem coisas diferentes de quem está olhando (CLAUDE.md, item 3).
      */}
      {isError ? (
        <ErrorState
          variant="load-failed"
          description={
            error?.aviso ??
            'Não foi possível buscar os credenciados agora. Verifique sua conexão e tente novamente.'
          }
          primaryAction={{ label: 'Tentar de novo', onClick: () => void refetch() }}
        />
      ) : (
        <>
          <div className="flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={alternarPagina}
              disabled={idsDaPagina.length === 0}
              className="text-muted-foreground hover:text-foreground text-xs underline-offset-2 hover:underline disabled:opacity-50 disabled:hover:no-underline"
            >
              {paginaToda ? 'Desmarcar esta página' : 'Selecionar todos desta página'}
              {idsDaPagina.length > 0 && ` (${idsDaPagina.length})`}
            </button>
            {selecionados.length > 0 && (
              <span className="text-muted-foreground text-xs" aria-live="polite">
                {selecionados.length} selecionado(s) no total
              </span>
            )}
          </div>

          <BarraDeAcoesEmLote
            selecionados={selecionados}
            linhasDaPagina={linhas}
            aoLimpar={() => setSelecionados([])}
          />

          <DataTable
            columns={tableView.orderedColumns as DataTableColumn<Credenciado>[]}
            personalizacao={tableView}
            data={linhas}
            isLoading={isLoading}
            total={data?.total}
            page={page}
            perPage={perPage}
            onPageChange={setPage}
            onPerPageChange={setPerPage}
            emptyIcon={Building2}
            emptyTitle="Nenhum credenciado encontrado"
            emptyDescription={
              temFiltro
                ? 'Nenhum credenciado atende a esses filtros. Tente afrouxar a busca.'
                : 'Quando houver credenciado com termo ativo, ele aparece aqui.'
            }
            minWidth="900px"
            ariaLabel="Lista de credenciados"
            skeletonColumns={7}
            selectedIds={selecionados}
            onTogglePageSelection={alternarPagina}
            onRowClick={(credenciado) => navigate(`/operacao/credenciados/${credenciado.slug}`)}
          />
        </>
      )}
    </div>
  );
}

export default CredenciadosPage;
